const MarkElements = {
  em: ['em', 0],
  strong: ['strong', 0],
  code: ['code', 0],
  underline: ['u', 0],
  strikethrough: ['del', 0],
  superscript: ['sup', 0],
  subscript: ['sub', 0],
  highlight: ['highlight', 0],
};


const supportAttributes = {
  href: {
    name: 'href',
    default: {
      default: null,
    },
  },
  title: {
    name: 'title',
    default: {
      default: null,
    },
  },
  class: {
    name: 'classes',
    jsName: 'classes',
    default: {
      default: null,
    },
  },
};

// Keeps track of supported and not supported attriutes
const attributeMap = {
  anchor: {
    allowedAttributes: [
      supportAttributes['href'],
      supportAttributes['title'],
    ],
    deniedAttributes: []
  }
};

/**
 * nodeName String
 * dom Object
 */
const returnSupportAttributes = (nodeName, dom) => {
  let attributesToReturn = {};
  const supportedAttributes = attributeMap[nodeName].allowedAttributes;
  for (let index = 0; index < supportedAttributes.length; index++) {
    const attribute = supportedAttributes[index];
    const name = attribute.jsName ? attribute.jsName : attribute.name;
    
    attributesToReturn[name] = dom.getAttribute(attribute.name);
  }

  return attributesToReturn;
}

// :: Object [Specs](#model.MarkSpec) for the marks in the schema.
const marks = {
  // :: MarkSpec A link. Has `href` and `title` attributes. `title`
  // defaults to the empty string. Rendered and parsed as an `<a>`
  // element.
  anchor: {
    attrs: {
      href: {},
      title: {default: null}
    },
    inclusive: false,
    parseDOM: [
      {
        tag: 'a[href]',
        getAttrs(dom) {
          return returnSupportAttributes('anchor', dom);
        }
      }
    ],
    toDOM(node) {
      let { href, title} = node.attrs;
      const target = '_blank';
      const rel = 'noopener noreferrer';
      return ['a', { href, title, target, rel }, 0];
    }
  },

  // :: MarkSpec An emphasis mark. Rendered as an `<em>` element.
  // Has parse rules that also match `<i>` and `font-style: italic`.
  em: {
    parseDOM: [{tag: 'i'}, {tag: 'em'}, {style: 'font-style=italic'}],
    toDOM() { return MarkElements.em }
  },

  // :: MarkSpec A strong mark. Rendered as `<strong>`, parse rules
  // also match `<b>` and `font-weight: bold`.
  strong: {
    parseDOM: [{tag: 'strong'},
               // This works around a Google Docs misbehavior where
               // pasted content will be inexplicably wrapped in `<b>`
               // tags with a font-weight normal.
               {tag: 'b', getAttrs: node => node.style.fontWeight != 'normal' && null},
               {style: 'font-weight', getAttrs: value => /^(bold(er)?|[5-9]\d{2,})$/.test(value) && null}],
    toDOM() { return MarkElements.strong }
  },

  // :: MarkSpec Code font mark. Represented as a `<code>` element.
  code: {
    parseDOM: [{tag: 'code'}],
    toDOM() { return MarkElements.code }
  },

  // :: MarkSpec An underline mark. Rendered as an `<u>` element.
  underline: {
    parseDOM: [{tag: 'u'}],
    toDOM() { return MarkElements.underline }
  },

  // :: MarkSpec An underline mark. Rendered as an `<u>` element.
  strikethrough: {
    parseDOM: [{tag: 'del'}],
    toDOM() { return MarkElements.strikethrough }
  },

  // :: MarkSpec An super mark. Rendered as an `<sup>` element.
  superscript: {
    parseDOM: [{tag: 'sup'}],
    toDOM() { return MarkElements.superscript }
  },

  // :: MarkSpec An subscript mark. Rendered as an `<sub>` element.
  subscript: {
    parseDOM: [{tag: 'sub'}],
    toDOM() { return MarkElements.subscript }
  },

  highlight: {
    attrs: {
      style: { default: 'background-color:yellow' },
      dryeditor: { default: 'highlight' },
    },
    parseDOM: [{tag: 'span[data-dry=highlight]', getAttrs(dom) {
      return {style: dom.getAttribute('style'), dryeditor: dom.getAttribute('data-dry')}
    }}],
    toDOM(node) {
      let { style, dryeditor } = node.attrs;
      return ['span', { style, dryeditor }, 0];
    }
  },

  backgroundcolor: {
    attrs: {
      color: { default: 'yellow' }, // any CSS color value supported; names, rgb, rgba, and hex colors
      dryeditor: { default: 'backgroundcolor' },
      reset: { default: false}
    },
    parseDOM: [{tag: 'span[data-dry=backgroundcolor]', getAttrs(dom) {
      return {style: dom.getAttribute('style'), dryeditor: dom.getAttribute('data-dry')}
    }}],
    toDOM(node) {
      let { color, dryeditor, reset } = node.attrs;
      if (!reset) {
        return ['span', {style: `background-color:${color}`, 'data-dry': dryeditor }, 0];
      }
      return ['span', { dryeditor }, 0];
    }
  },
  fontfamily: {
    attrs: {
      fontFamily: { default: 'inherit' },
      dryeditor: { default: 'fontfamily' },
    },
    parseDOM: [{
      tag: 'span[data-dry=fontfamily]',
      getAttrs(dom) {
        return { fontFamily: dom.style.fontFamily, dryeditor: dom.getAttribute('data-dry') };
      }
    }],
    toDOM(node) {
      const { fontFamily, dryeditor } = node.attrs;
      return ['span', { style: `font-family:${fontFamily}`, 'data-dry': dryeditor }, 0];
    }
  },

  fontsize: {
    attrs: {
      fontSize: { default: 'inherit' },
      dryeditor: { default: 'fontsize' },
    },
    parseDOM: [{
      tag: 'span[data-dry=fontsize]',
      getAttrs(dom) {
        return { fontSize: dom.style.fontSize, dryeditor: dom.getAttribute('data-dry') };
      }
    }],
    toDOM(node) {
      const { fontSize, dryeditor } = node.attrs;
      return ['span', { style: `font-size:${fontSize}`, 'data-dry': dryeditor }, 0];
    }
  },

  fontcolor: {
    attrs: {
      color: { default: 'black' }, // any CSS color value supported; names, rgb, rgba, and hex colors
      dryeditor: { default: 'fontcolor' },
      reset: { default: false}
    },
    parseDOM: [{tag: 'span[data-dry=fontcolor]', getAttrs(dom) {
      return {style: dom.getAttribute('style'), dryeditor: dom.getAttribute('data-dry')}
    }}],
    toDOM(node) {
      let { color, dryeditor, reset } = node.attrs;
      if (!reset) {
        return ['span', {style: `color:${color}`, 'data-dry': dryeditor }, 0];
      }
      return ['span', { dryeditor }, 0];
    }
  },

  // a
// abbr
// b
// bdi
// bdo
// br
// cite
// code
// data
// dfn
// em
// i
// kbd
// mark
// q
// rb
// rp
// rt
// rtc
// ruby
// s
// samp
// small
// span
// strong
// sub
// time
// u
// var
// wbr
};

export {
  MarkElements
};
export default marks;
